import { useState, useEffect, useRef } from 'react';
import api, { API_BASE_URL } from '../api';
import { useNetworkSync } from './useNetworkSync';
import { isOnline, queueStrengthUpdate, queueNutritionUpdate } from '../offline/sync';
import { setCache, getCache } from '../offline/store';
import { getLabels } from '../constants/labels';
import {
  isValidClassLevel,
  buildDivisionName,
} from '../constants/classLevels';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { sortByDivisionName } from '../utils/sortDivisionNames';
import { getIstDateIso } from '../utils/istTime';
import {
  canWriteWithSubscription,
  defaultTrialEndDate,
  getSubscriptionWarning,
} from '../utils/academicYear';
import { getExportExtension } from '../utils/exportDocument';
import { createExportBlob, createNutritionAllExportBlob } from '../utils/exportTabular';
import { exportFile } from '../utils/nativeExport';
import { toDateIso, normalizeDateIso } from '../utils/dateIso';
import { EMPTY_NUTRITION_FORM } from '../components/NutritionTables';
import { assertLoginRoleAllowed, isAdminLoginAllowed } from '../utils/loginRoles';
import { wantsPersistentSession } from '../utils/clientSession';
import { storageGet, storageSet, storageRemove, isStorageAvailable } from '../utils/safeStorage';
import { unregisterPushToken } from '../push/registerPush';
import { formatLoginFailureMessage } from '../utils/loginErrorReport';
import {
  getDefaultInvoiceIssuer,
  mergeInvoiceIssuer,
  normalizeInvoiceIssuer,
  pickIssuerPayload,
  writeCachedInvoiceIssuer,
} from '../constants/invoiceIssuer';

const HEAD_HISTORY_DATE_KEY = 'nmhelper_head_history_date';
const HEAD_HISTORY_MONTH_KEY = 'nmhelper_head_history_month';
const TEACHER_HISTORY_DATE_KEY = 'nmhelper_teacher_history_date';
const TEACHER_HISTORY_MONTH_KEY = 'nmhelper_teacher_history_month';

export function useAppState() {
  const isNetworkFailure = (error) => {
    if (!error) return false;
    if (!error.response) return true;
    const msg = String(error.message || '').toLowerCase();
    return msg.includes('network') || msg.includes('timeout') || msg.includes('failed to fetch');
  };

  const { success: toastSuccess, error: toastError } = useToast();
  const { confirm: confirmDialog } = useConfirm();

  const [useMalayalam, setUseMalayalam] = useState(false);
  const labels = getLabels(useMalayalam);

  const { online, pending, syncing, syncNow, refreshPending, lastSyncError } = useNetworkSync({
    labels,
    toastSuccess,
    toastError,
  });
  const [role, setRole] = useState('');
  const [token, setToken] = useState(() => storageGet('nmhelper_token') || '');
  const [userInfo, setUserInfo] = useState(null); // Store logged-in user info
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [strengths, setStrengths] = useState([]);
  const [historicalStrengths, setHistoricalStrengths] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const saved = storageGet(HEAD_HISTORY_MONTH_KEY);
    if (saved && /^\d{4}-\d{2}$/.test(saved)) return saved;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedDate, setSelectedDate] = useState(''); // Used for both teacher update and historical view
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  // const [unassignedClasses, setUnassignedClasses] = useState([]); // FIX: Removed unused state
  const [strengthForm, setStrengthForm] = useState({ class_id: '', division_name: '', boys: '', girls: '', max_students: '', max_boys: '', max_girls: '' });
  const [nutritionForm, setNutritionForm] = useState({ ...EMPTY_NUTRITION_FORM });
  const [nutritionRecords, setNutritionRecords] = useState([]);
  const [nutritionReport, setNutritionReport] = useState(null);
  const [nutritionHistoricalDates, setNutritionHistoricalDates] = useState([]);
  const [nutritionHistoricalReport, setNutritionHistoricalReport] = useState(null);
  const [selectedNutritionDate, setSelectedNutritionDate] = useState('');
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [teacherSchoolClasses, setTeacherSchoolClasses] = useState([]);
  const [schoolHeads, setSchoolHeads] = useState([]);
  const [invoiceIssuer, setInvoiceIssuer] = useState(() => mergeInvoiceIssuer({}));
  const [testerSignups, setTesterSignups] = useState([]);
  const [headDivisions, setHeadDivisions] = useState([]); // Added missing state declarations
  const [showAddHeadForm, setShowAddHeadForm] = useState(false);
  const [newHead, setNewHead] = useState({
    name: '',
    username: '',
    password: '',
    divisions: '',
    subscriptionExpiresAt: '',
  });
  const [newTeacher, setNewTeacher] = useState({ name: '', login_suffix: '', password: '', class_id: '' });
  const [newClass, setNewClass] = useState({
    class_number: '',
    division_letter: '',
    teacher_id: '',
    max_boys: '',
    max_girls: '',
  });
  const [isLoading, setIsLoading] = useState({
    assignTeacher: false,
    report: false,
    teachers: false,
    classes: false,
    login: false,
    heads: false,
    testerSignups: false,
    addHead: false,
    historicalReport: false,
    monthlyReport: false,
    updateCapacity: false,
    addTeacher: false,
    resetTeacherPassword: false,
    updateTeacherName: false,
    headUpdateStrength: false,
    resetHeadPassword: false,
    addClass: false,
    setDivisions: false,
    setSubscription: false,
    deleteHead: false,
    deleteTeacher: false,
    deleteClass: false,
    updateStrength: false,
    updateNutrition: false,
    nutritionReport: false,
    nutritionHistorical: false,
    deleteNutritionHistorical: false,
    deleteHistorical: false, // <-- Add this line
    invoiceIssuer: false,
  });

  const [strengthStatus, setStrengthStatus] = useState([]);
  const [showStrengthStatus, setShowStrengthStatus] = useState(false);
  const [isLoadingStrengthStatus, setIsLoadingStrengthStatus] = useState(false);
  const [teacherSubmissionStatus, setTeacherSubmissionStatus] = useState(null);
  const [isLoadingTeacherSubmission, setIsLoadingTeacherSubmission] = useState(false);
  const [headUsernameForDivisions, setHeadUsernameForDivisions] = useState(''); // Renamed for clarity
  const [numberOfDivisions, setNumberOfDivisions] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginErrorMeta, setLoginErrorMeta] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const sessionBootstrapped = useRef(false);

  useEffect(() => {
    const stored = storageGet('nmhelper_user');
    if (token && stored) {
      try {
        const user = JSON.parse(stored);
        if (user.role === 'admin' && !isAdminLoginAllowed()) {
          storageRemove('nmhelper_token');
          storageRemove('nmhelper_user');
          setToken('');
          setUserInfo(null);
          setRole('');
          setLoginError('Admin login is not available in this app. Use the web app at app.nmhelper.in.');
          return;
        }
        setUserInfo(user);
        if (user.role) setRole(user.role);
      } catch {
        /* ignore */
      }
    }
  }, [token]);

  const subscriptionExpiresAt = userInfo?.subscription_expires_at
    ? String(userInfo.subscription_expires_at).slice(0, 10)
    : null;
  const subscriptionWritesBlocked = !canWriteWithSubscription(subscriptionExpiresAt);
  const subscriptionWarning = getSubscriptionWarning(subscriptionExpiresAt);

  const blockSubscriptionWrite = () => {
    toastError(labels.subscriptionWritesBlocked);
    return true;
  };

  useEffect(() => {
    const root = document.documentElement;
    root.lang = useMalayalam ? 'ml' : 'en';
    root.classList.toggle('locale-ml', useMalayalam);
  }, [useMalayalam]);


  // --- API Functions (Keep most as is, update delete functions) ---

  

  const login = async () => {
    setIsLoading(prev => ({...prev, login: true}));
    setLoginError('');
    setLoginErrorMeta(null);

    if (!role) {
        setLoginError('Please select a role.');
        setIsLoading(prev => ({...prev, login: false}));
        return;
    }
    if (!assertLoginRoleAllowed(role)) {
      setLoginError('Admin login is not available in this app. Use the web app at app.nmhelper.in.');
      setIsLoading(prev => ({ ...prev, login: false }));
      return;
    }
    const username = String(loginData.username || '').trim();
    if (!username || !loginData.password) {
      setLoginError('Please enter both username and password.');
      setIsLoading(prev => ({...prev, login: false}));
      return;
    }
    if (username !== loginData.username) {
      setLoginData((prev) => ({ ...prev, username }));
    }

    try {
      // Soft health check — do not block login on flaky iOS Safari /health.
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);
        await fetch(`${API_BASE_URL}/health`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        });
        clearTimeout(timer);
      } catch (healthErr) {
        console.warn('Server health check skipped:', healthErr?.message || healthErr);
      }

      const res = await api.request({
        method: 'POST',
        url: `/${role}/login`,
        headers: {'Content-Type': 'application/json'},
        data: {
          username,
          password: loginData.password,
          persistent_session: wantsPersistentSession(),
        },
        timeout: 15000
      });

      if (!res.data?.success || !res.data.token || !res.data.user?.role) {
        throw new Error(res.data?.message || 'Invalid login response from server.');
      }

      if (res.data.user.role !== role) {
        throw new Error(`Login successful, but your role is '${res.data.user.role}', not '${role}'. Please select the correct role.`);
      }

      setToken(res.data.token);
      const savedOk =
        storageSet('nmhelper_token', res.data.token) &&
        storageSet('nmhelper_user', JSON.stringify(res.data.user));
      if (!savedOk || !isStorageAvailable()) {
        setLoginError(labels.storageBlocked);
      }
      setUserInfo(res.data.user);
      setIsLoading((prev) => ({ ...(typeof prev === 'object' && prev ? prev : {}), login: true }));

      // Fetch initial data based on role AFTER successful login
      if (res.data.user.role === 'head') {
        // Use Promise.allSettled to fetch even if one fails
        const results = await Promise.allSettled([
            fetchReport(res.data.token), // Pass token directly
            fetchTeachers(res.data.token),
            fetchClasses(res.data.token)
        ]);
        results.forEach((result, index) => {
            if (result.status === 'rejected') {
                const fetchName = ['report', 'teachers', 'classes'][index];
                console.error(`Failed to fetch initial ${fetchName} data:`, result.reason);
                // Optionally show a non-blocking error to the user
            }
        });
      } else if (res.data.user.role === 'teacher') {
            try {
                await loadTeacherClasses(res.data.token);
                setSelectedDate(getIstDateIso());
            } catch (err) {
                console.error('Teacher initialization error:', err);
                setLoginError(`Logged in, but failed to load class data: ${err.response?.data?.error || err.message}`);
            }
      } else if (res.data.user.role === 'admin') {
          // Fetch initial admin data if needed (e.g., school heads)
          try {
            await fetchSchoolHeads(res.data.token);
          } catch (err) {
            console.error('Admin initialization error:', err);
            setLoginError(`Logged in, but failed to load admin data: ${err.response?.data?.error || err.message}`);
          }
      }

      void syncNow({ silent: true });
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = formatLoginFailureMessage({
        err,
        labels,
        role,
        username,
      });
      setLoginError(errorMessage);
      setLoginErrorMeta({
        errorCode: err.response?.data?.code || null,
        httpStatus: err.response?.status ?? null,
        axiosCode: err.code || null,
        rawMessage: err.message || null,
      });
      // Only wipe session if login itself failed (token never accepted).
      if (!storageGet('nmhelper_token')) {
        setToken('');
        storageRemove('nmhelper_token');
        storageRemove('nmhelper_user');
      }
    } finally {
      setIsLoading(prev => ({ ...(typeof prev === 'object' && prev ? prev : {}), login: false }));
    }
  };

  // --- Other fetch/update functions (ensure they use the 'token' state variable) ---
  // Example: fetchTeachers should use the token from state
  const fetchTeachers = async (currentToken = token) => {
    if (!currentToken) return;
    setIsLoading(prev => ({...prev, teachers: true}));
    try {
      if (!isOnline()) {
        const cached = await getCache('head_teachers');
        if (cached) {
          setTeachers(cached);
          return;
        }
        throw new Error('Offline and no cached teachers');
      }
      const res = await api.get('/head/teachers');
      const list = res.data.teachers || [];
      await setCache('head_teachers', list);
      setTeachers(list);
    } catch (err) {
      console.error('Fetch teachers error:', err);
      setStatusMessage(err.response?.data?.error || err.message || 'Failed to fetch teachers');
    } finally {
      setIsLoading(prev => ({...prev, teachers: false }));
    }
  };
    // Example: fetchHistoricalReport
    const fetchHistoricalReport = async (date) => {
      if (!token || !date) return;
      const formattedDate = toDateIso(date);
      if (!formattedDate) return;
      localStorage.setItem(HEAD_HISTORY_DATE_KEY, formattedDate);
      setIsLoading(prev => ({ ...prev, historicalReport: true }));
      try {
        const res = await api.get(`/head/historical-report`, {
          params: { date: formattedDate },
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Historical report response data:', res.data);
        // Accept array directly or object with data property
        const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
        if (!Array.isArray(data)) {
          throw new Error("Invalid historical report data format received");
        }
        // Deduplicate data by class_id and date, keep latest by id
        const uniqueMap = new Map();
        data.forEach(item => {
          const key = `${item.class_id}-${item.date}`;
          if (!uniqueMap.has(key)) {
            uniqueMap.set(key, item);
          } else {
            // Keep item with higher id (assuming higher id is latest)
            if (item.id > uniqueMap.get(key).id) {
              uniqueMap.set(key, item);
            }
          }
        });
        const deduplicatedData = Array.from(uniqueMap.values());
        setHistoricalStrengths(deduplicatedData);
      } catch (err) {
        console.error('Fetch historical report error:', err);
        toastError(err.response?.data?.error || err.message || 'Failed to fetch historical report');
        setHistoricalStrengths([]); // Clear on error
      } finally {
        setIsLoading(prev => ({ ...prev, historicalReport: false }));
      }
    };
  
    // --- ADD THIS FUNCTION ---
    const deleteHistoricalReport = async (reportId, reportDate) => {
      console.log('Token at deleteHistoricalReport:', token);
      console.log('Attempting to delete historical report with id:', reportId, 'and date:', reportDate);
      if (!token || !reportId || !reportDate) {
        console.warn('Missing token, reportId or reportDate, cannot delete historical report');
        return;
      }
  
      if (!await confirmDialog(useMalayalam ? `ഈ ചരിത്ര റിപ്പോർട്ട് എൻട്രി ഇല്ലാതാക്കണോ?` : `Delete this historical report entry? This cannot be undone.`)) {
        return;
      }
  
      setIsLoading(prev => ({ ...prev, deleteHistorical: true })); // Start loading
  
      try {
        // Convert reportDate to ISO format YYYY-MM-DD for backend
        // Delete single historical report entry by ID, no date needed
        const res = await api.delete(`/head/historical-report-id/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Delete historical report response:', res.data);
  
        if (res.data.success) {
          toastSuccess(labels.toastHistoryDeleted);
          // Update the state to remove the deleted item from the view
          setHistoricalStrengths(prev => prev.filter(item => item.id !== reportId));
        } else {
          throw new Error(res.data.error || 'Deletion failed');
        }
      } catch (err) {
        console.error('Delete historical report error:', err);
        toastError(err.response?.data?.error || err.message || 'Failed to delete historical report entry');
      } finally {
        setIsLoading(prev => ({ ...prev, deleteHistorical: false })); // Stop loading
      }
    };
    // --- END OF ADDED FUNCTION ---
  
    // Example: updateClassCapacity (Keep as is)
    const updateClassCapacity = async (classId, { max_boys, max_girls }) => {
      if (!token) return false;
      setIsLoading(prev => ({ ...prev, updateCapacity: true }));
      try {
        const payload = {
          max_boys: parseInt(max_boys, 10),
          max_girls: parseInt(max_girls, 10),
        };
        const res = await api.patch(`/head/classes/${classId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          toastSuccess(labels.toastCapacityUpdated);
          setClasses(prevClasses =>
            prevClasses.map(cls =>
              cls.id === classId
                ? {
                    ...cls,
                    max_boys: res.data.max_boys,
                    max_girls: res.data.max_girls,
                    max_students: res.data.max_students,
                  }
                : cls
            )
          );
          return true;
        }
        throw new Error(res.data.error || 'Failed to update class capacity');
      } catch (err) {
        console.error('Update class capacity error:', err);
        toastError(err.response?.data?.message || err.response?.data?.error || err.message || (useMalayalam ? 'ക്ലാസ് പരമാവധി അപ്‌ഡേറ്റ് ചെയ്യുന്നതിൽ പിശക്' : 'Failed to update class limits'));
        return false;
      } finally {
        setIsLoading(prev => ({ ...prev, updateCapacity: false }));
      }
    };
  
   
  // Example: fetchClasses
  const fetchClasses = async (currentToken = token) => {
    if (!currentToken) return;
    setIsLoading(prev => ({...prev, classes: true }));
    try {
      if (!isOnline()) {
        const cached = await getCache('head_classes');
        if (cached) {
          setClasses(sortByDivisionName(cached));
          return;
        }
        throw new Error('Offline and no cached classes');
      }
      const res = await api.get('/head/classes');
      const processedClasses = sortByDivisionName(
        res.data.map((cls) => ({
          ...cls,
          teacher_name: cls.teacher_id
            ? cls.teacher_name || `Assigned (ID: ${cls.teacher_id})`
            : 'Not assigned',
        }))
      );
      await setCache('head_classes', processedClasses);
      setClasses(processedClasses);
    } catch (err) {
      console.error('Fetch classes error:', err);
      setStatusMessage(
        err.response?.data?.error ||
          err.message ||
          (useMalayalam ? 'ക്ലാസുകൾ ലഭ്യമാക്കുന്നതിൽ പിശക്' : 'Failed to fetch classes')
      );
    } finally {
      setIsLoading(prev => ({ ...prev, classes: false }));
    }
  };

  // Example: fetchReport
  const fetchReport = async (currentToken = token) => {
    if (!currentToken) return;
    setIsLoading(prev => ({ ...prev, report: true }));
    try {
      const today = toDateIso(new Date());
      if (!isOnline()) {
        const cached = await getCache(`head_report_${today}`);
        if (cached) {
          setStrengths(cached);
          return;
        }
        throw new Error('Offline and no cached report');
      }
      const res = await api.get('/head/report', { params: { date: today } });
      if (!res.data || !Array.isArray(res.data)) {
        throw new Error('Invalid report data format received');
      }
      await setCache(`head_report_${today}`, res.data);
      setStrengths(res.data);
    } catch (err) {
      console.error('Report fetch error:', err);
      setStatusMessage(err.response?.data?.error || err.message || "Failed to fetch today's report");
      setStrengths([]);
    } finally {
      setIsLoading(prev => ({ ...prev, report: false }));
    }
  };



  // Example: updateStrength (Teacher)
  const updateStrength = async () => {
    if (subscriptionWritesBlocked) {
      blockSubscriptionWrite();
      return;
    }
    if (!token || !selectedDate) {
      toastError(useMalayalam ? 'ദയവായി ഒരു തീയതി തിരഞ്ഞെടുക്കുക' : 'Please select a date');
      return;
    }
    // Basic validation
    const boys = parseInt(strengthForm.boys, 10);
    const girls = parseInt(strengthForm.girls, 10);
    if (isNaN(boys) || boys < 0 || isNaN(girls) || girls < 0) {
        toastError(useMalayalam ? 'ദയവായി ആൺകുട്ടികളുടെയും പെൺകുട്ടികളുടെയും എണ്ണം ശരിയായി നൽകുക' : 'Please enter valid numbers for boys and girls.');
        return;
    }
    const maxBoys = parseInt(strengthForm.max_boys, 10) || 0;
    const maxGirls = parseInt(strengthForm.max_girls, 10) || 0;
    const maxStudents = parseInt(strengthForm.max_students, 10) || 0;
    if (maxBoys > 0 && boys > maxBoys) {
      toastError(labels.toastBoysCapExceeded + (useMalayalam ? ` (${maxBoys})` : ` (max ${maxBoys})`));
      return;
    }
    if (maxGirls > 0 && girls > maxGirls) {
      toastError(labels.toastGirlsCapExceeded + (useMalayalam ? ` (${maxGirls})` : ` (max ${maxGirls})`));
      return;
    }
    if (maxBoys === 0 && maxGirls === 0 && maxStudents > 0 && boys + girls > maxStudents) {
      toastError(
        useMalayalam
          ? `ആകെ ${maxStudents} ൽ കൂടുതൽ നൽകാൻ കഴിയില്ല`
          : `Total cannot exceed ${maxStudents} students`
      );
      return;
    }

    setIsLoading(prev => ({ ...prev, updateStrength: true }));
    setStatusMessage('');
    try {
      const formattedDate = toDateIso(selectedDate);
      const payload = {
        class_id: strengthForm.class_id,
        boys,
        girls,
        date: formattedDate,
      };

      if (!online) {
        try {
          await queueStrengthUpdate(payload);
        } catch (e) {
          if (e.message === 'SUBSCRIPTION_EXPIRED') {
            toastError(labels.subscriptionWritesBlocked);
            return;
          }
          throw e;
        }
        await refreshPending();
        void syncNow({ silent: true });
        setStatusMessage(
          useMalayalam
            ? 'ഓഫ്‌ലൈനിൽ സേവ് ചെയ്തു. ഇന്റർനെറ്റ് വന്നാൽ സമന്വയിക്കും.'
            : 'Saved offline. Will sync when you are back online.'
        );
        return;
      }

      const response = await api.post('/teacher/update-strength', payload);
      if (response.data.success) {
        toastSuccess(labels.toastStrengthSaved);
        setStatusMessage(response.data.message || labels.toastStrengthSaved);
        fetchTeacherSubmissionStatus(formattedDate);
      } else {
        throw new Error(response.data.error || 'Failed to update NM present');
      }
    } catch (error) {
      console.error('Update strength error:', error);
      if (isNetworkFailure(error)) {
        try {
          const formattedDate = toDateIso(selectedDate);
          await queueStrengthUpdate({
            class_id: strengthForm.class_id,
            boys,
            girls,
            date: formattedDate,
          });
          await refreshPending();
          void syncNow({ silent: true });
          setStatusMessage(
            useMalayalam
              ? 'നെറ്റ്‌വർക്ക് ഇല്ല. ഓഫ്‌ലൈനിൽ സേവ് ചെയ്തു; പിന്നീട് സ്വയം സമന്വയിക്കും.'
              : 'No network. Saved offline and will sync automatically.'
          );
          return;
        } catch (queueErr) {
          console.error('Offline queue fallback failed:', queueErr);
        }
      }
      setStatusMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'An error occurred while updating strength'
      );
    } finally {
      setIsLoading(prev => ({ ...prev, updateStrength: false }));
    }
  };

  const fetchTeacherNutrition = async (classId, dateIso) => {
    if (!token || !classId) return;
    const date = dateIso || (selectedDate ? toDateIso(selectedDate) : getIstDateIso());
    try {
      const res = await api.get('/teacher/nutrition', {
        params: { class_id: classId, date },
      });
      if (res.data?.success !== false) {
        const form = { ...EMPTY_NUTRITION_FORM };
        (res.data.records || []).forEach((record) => {
          if (record.item_type) {
            form[`${record.item_type}_boys`] =
              record.boys != null ? String(record.boys) : '';
            form[`${record.item_type}_girls`] =
              record.girls != null ? String(record.girls) : '';
          }
        });
        setNutritionForm(form);
        setNutritionRecords(res.data.records || []);
      }
    } catch (err) {
      console.error('fetchTeacherNutrition error:', err);
      setNutritionForm({ ...EMPTY_NUTRITION_FORM });
      setNutritionRecords([]);
    }
  };

  const parseNutritionPair = (itemType) => {
    const boysRaw = nutritionForm[`${itemType}_boys`].trim();
    const girlsRaw = nutritionForm[`${itemType}_girls`].trim();
    if (boysRaw === '' && girlsRaw === '') return null;
    if (boysRaw === '' || girlsRaw === '') {
      toastError(
        useMalayalam
          ? `${itemType} — ആൺ, പെൺ എണ്ണം രണ്ടും നൽകുക`
          : `Enter both boys and girls for ${itemType}`
      );
      return undefined;
    }
    const boys = parseInt(boysRaw, 10);
    const girls = parseInt(girlsRaw, 10);
    if (Number.isNaN(boys) || Number.isNaN(girls) || boys < 0 || girls < 0) {
      toastError(labels.toastInvalidNutritionCount);
      return undefined;
    }
    const maxBoys = parseInt(strengthForm.max_boys, 10) || 0;
    const maxGirls = parseInt(strengthForm.max_girls, 10) || 0;
    const maxStudents = parseInt(strengthForm.max_students, 10) || 0;
    if (maxBoys > 0 && boys > maxBoys) {
      toastError(labels.toastBoysCapExceeded + (useMalayalam ? ` (${maxBoys})` : ` (max ${maxBoys})`));
      return undefined;
    }
    if (maxGirls > 0 && girls > maxGirls) {
      toastError(labels.toastGirlsCapExceeded + (useMalayalam ? ` (${maxGirls})` : ` (max ${maxGirls})`));
      return undefined;
    }
    if (maxBoys === 0 && maxGirls === 0 && maxStudents > 0 && boys + girls > maxStudents) {
      toastError(
        useMalayalam
          ? `ആകെ ${maxStudents} ൽ കൂടുതൽ നൽകാൻ കഴിയില്ല`
          : `Total cannot exceed ${maxStudents} students`
      );
      return undefined;
    }
    return { boys, girls };
  };

  const submitNutritionUpdate = async (updates) => {
    if (subscriptionWritesBlocked) {
      blockSubscriptionWrite();
      return false;
    }
    if (!token || !strengthForm.class_id || !selectedDate) {
      toastError(useMalayalam ? 'ദയവായി ഒരു തീയതി തിരഞ്ഞെടുക്കുക' : 'Please select a date');
      return false;
    }
    if (!updates || Object.keys(updates).length === 0) {
      return false;
    }

    setIsLoading((prev) => ({ ...prev, updateNutrition: true }));
    const formattedDate = toDateIso(selectedDate);
    const payload = {
      class_id: strengthForm.class_id,
      date: formattedDate,
      ...updates,
    };

    try {
      if (!online) {
        await queueNutritionUpdate(payload);
        await refreshPending();
        void syncNow({ silent: true });
        setStatusMessage(
          useMalayalam
            ? 'ഓഫ്‌ലൈനിൽ സേവ് ചെയ്തു. ഇന്റർനെറ്റ് വന്നാൽ സമന്വയിക്കും.'
            : 'Saved offline. Will sync when you are back online.'
        );
        return true;
      }

      const response = await api.post('/teacher/update-nutrition', payload);
      if (response.data.success) {
        toastSuccess(labels.toastNutritionSaved);
        await fetchTeacherNutrition(strengthForm.class_id, formattedDate);
        return true;
      }
      throw new Error(response.data.message || 'Failed to update nutrition');
    } catch (error) {
      console.error('Update nutrition error:', error);
      if (isNetworkFailure(error)) {
        try {
          await queueNutritionUpdate(payload);
          await refreshPending();
          void syncNow({ silent: true });
          setStatusMessage(
            useMalayalam
              ? 'നെറ്റ്‌വർക്ക് ഇല്ല. ഓഫ്‌ലൈനിൽ സേവ് ചെയ്തു.'
              : 'No network. Saved offline.'
          );
          return true;
        } catch (queueErr) {
          console.error('Offline nutrition queue failed:', queueErr);
        }
      }
      toastError(
        error.response?.data?.message ||
          error.message ||
          (useMalayalam ? 'പോഷകാഹാരം അപ്‌ഡേറ്റ് ചെയ്യാൻ കഴിഞ്ഞില്ല' : 'Could not update nutrition')
      );
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, updateNutrition: false }));
    }
  };

  const updateNutritionItem = async (itemType) => {
    const pair = parseNutritionPair(itemType);
    if (pair === undefined) return false;
    if (!pair) {
      toastError(labels.toastNutritionEnterCount);
      return false;
    }
    return submitNutritionUpdate({ [itemType]: pair });
  };

  const updateNutritionMilk = async () => updateNutritionItem('milk');

  const clearNutritionItems = async (items) => {
    if (!items?.length) return false;
    const updates = {};
    items.forEach((item) => {
      updates[item] = null;
    });
    const ok = await submitNutritionUpdate(updates);
    if (ok) {
      setNutritionForm((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          next[`${item}_boys`] = '';
          next[`${item}_girls`] = '';
        });
        return next;
      });
      await fetchTeacherNutrition(strengthForm.class_id, toDateIso(selectedDate));
    }
    return ok;
  };

  const fetchNutritionReport = async (dateIso) => {
    if (!token) return;
    const date = dateIso || getIstDateIso();
    setIsLoading((prev) => ({ ...prev, nutritionReport: true }));
    try {
      const res = await api.get('/head/nutrition-report', { params: { date } });
      setNutritionReport(res.data);
    } catch (err) {
      console.error('fetchNutritionReport error:', err);
      setNutritionReport(null);
      toastError(useMalayalam ? 'ഡാറ്റ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല' : 'Could not load data');
    } finally {
      setIsLoading((prev) => ({ ...prev, nutritionReport: false }));
    }
  };

  const fetchNutritionHistoricalDates = async () => {
    if (!token) return [];
    try {
      const res = await api.get('/head/nutrition-historical-dates');
      const dates = (res.data?.dates || [])
        .map(normalizeDateIso)
        .filter(Boolean);
      setNutritionHistoricalDates(dates);
      return dates;
    } catch (err) {
      console.error('fetchNutritionHistoricalDates error:', err);
      setNutritionHistoricalDates([]);
      return [];
    }
  };

  const fetchNutritionHistoricalReport = async (dateIso) => {
    if (!token || !dateIso) return;
    setIsLoading((prev) => ({ ...prev, nutritionHistorical: true }));
    try {
      const res = await api.get('/head/nutrition-historical-report', {
        params: { date: dateIso },
      });
      setNutritionHistoricalReport(res.data);
    } catch (err) {
      console.error('fetchNutritionHistoricalReport error:', err);
      setNutritionHistoricalReport(null);
    } finally {
      setIsLoading((prev) => ({ ...prev, nutritionHistorical: false }));
    }
  };

  const deleteNutritionHistorical = async (id, dateIso) => {
    if (!token || !id) return false;
    setIsLoading((prev) => ({ ...prev, deleteNutritionHistorical: true }));
    try {
      await api.delete(`/head/nutrition-historical-id/${id}`);
      toastSuccess(labels.toastHistoryDeleted);
      if (dateIso) {
        await fetchNutritionHistoricalReport(dateIso);
        await fetchNutritionHistoricalDates();
      }
      return true;
    } catch (err) {
      console.error('deleteNutritionHistorical error:', err);
      toastError(useMalayalam ? 'നീക്കം ചെയ്യാൻ കഴിഞ്ഞില്ല' : 'Could not delete');
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, deleteNutritionHistorical: false }));
    }
  };

  const fetchTeacherSchoolClasses = async () => {
    if (!token) return [];
    try {
      const res = await api.get('/teacher/school-classes');
      const list = sortByDivisionName(res.data?.classes || []);
      setTeacherSchoolClasses(list);
      return list;
    } catch (err) {
      console.error('fetchTeacherSchoolClasses error:', err);
      setTeacherSchoolClasses([]);
      return [];
    }
  };

  const fetchTeacherHistoricalReport = async (date) => {
    if (!token || !date) return;
    const formattedDate = toDateIso(date);
    if (!formattedDate) return;
    localStorage.setItem(TEACHER_HISTORY_DATE_KEY, formattedDate);
    setIsLoading((prev) => ({ ...prev, historicalReport: true }));
    try {
      const res = await api.get('/teacher/historical-report', {
        params: { date: formattedDate },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setHistoricalStrengths(data);
    } catch (err) {
      console.error('fetchTeacherHistoricalReport error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to fetch historical report');
      setHistoricalStrengths([]);
    } finally {
      setIsLoading((prev) => ({ ...prev, historicalReport: false }));
    }
  };

  const fetchTeacherMonthlyReport = async (yearMonth) => {
    if (!token) return;
    const ym = yearMonth || selectedMonth;
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) {
      toastError(useMalayalam ? 'മാസം തിരഞ്ഞെടുക്കുക' : 'Please select a month');
      return;
    }
    localStorage.setItem(TEACHER_HISTORY_MONTH_KEY, ym);
    const [year, month] = ym.split('-').map((n) => parseInt(n, 10));
    setIsLoading((prev) => ({ ...prev, monthlyReport: true }));
    try {
      const res = await api.get('/teacher/monthly-report', {
        params: { year, month },
      });
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Failed to load monthly report');
      }
      setMonthlyReport(res.data);
      setSelectedMonth(ym);
    } catch (err) {
      console.error('fetchTeacherMonthlyReport error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to load monthly report');
      setMonthlyReport(null);
    } finally {
      setIsLoading((prev) => ({ ...prev, monthlyReport: false }));
    }
  };

  const fetchTeacherNutritionHistoricalDates = async () => {
    if (!token) return [];
    try {
      const res = await api.get('/teacher/nutrition-historical-dates');
      const dates = (res.data?.dates || []).map(normalizeDateIso).filter(Boolean);
      setNutritionHistoricalDates(dates);
      return dates;
    } catch (err) {
      console.error('fetchTeacherNutritionHistoricalDates error:', err);
      setNutritionHistoricalDates([]);
      return [];
    }
  };

  const fetchTeacherNutritionHistoricalReport = async (dateIso) => {
    if (!token || !dateIso) return;
    setIsLoading((prev) => ({ ...prev, nutritionHistorical: true }));
    try {
      const res = await api.get('/teacher/nutrition-historical-report', {
        params: { date: dateIso },
      });
      setNutritionHistoricalReport(res.data);
    } catch (err) {
      console.error('fetchTeacherNutritionHistoricalReport error:', err);
      setNutritionHistoricalReport(null);
    } finally {
      setIsLoading((prev) => ({ ...prev, nutritionHistorical: false }));
    }
  };

  const exportNutritionData = async ({ itemType, rows, fileType, dateIso, isConsolidated = false }) => {
    if (!rows?.length) {
      toastError(useMalayalam ? 'എക്സ്പോർട്ടിന് ഡാറ്റയില്ല' : 'No data to export');
      return;
    }
    const itemLabel = labels[`nutrition${itemType.charAt(0).toUpperCase()}${itemType.slice(1)}`] || itemType;
    const title = isConsolidated
      ? `${itemLabel} Consolidated ${dateIso}`
      : `${itemLabel} ${dateIso}`;
    const headers = isConsolidated
      ? [labels.classNumber, labels.boys, labels.girls, labels.totalStudents]
      : [labels.division, labels.boys, labels.girls, labels.totalStudents];

    const exportRows = rows.map((row) =>
      isConsolidated
        ? {
            [labels.classNumber]: row.group,
            [labels.boys]: row.boys,
            [labels.girls]: row.girls,
            [labels.totalStudents]: row.total,
          }
        : {
            [labels.division]: row.division_name,
            [labels.boys]: row.boys,
            [labels.girls]: row.girls,
            [labels.totalStudents]: row.total,
          }
    );

    const { blob, mimeType } = createExportBlob({
      fileType,
      title,
      headers,
      rows: exportRows,
      pdfOrientation: 'auto',
      pdfMeta: {
        appName: labels.appTitle || 'NMhelper',
        schoolName: userInfo?.name || userInfo?.username || '',
        exportedAt: dateIso,
      },
    });
    const ext = getExportExtension(fileType);
    const mode = isConsolidated ? 'consolidated' : 'by_division';
    try {
      await exportFile({
        blob,
        fileName: `nutrition_${itemType}_${mode}_${dateIso}.${ext}`,
        mimeType,
      });
      toastSuccess(labels.toastExportDone);
    } catch (err) {
      console.error('Nutrition export failed:', err);
      toastError(useMalayalam ? 'എക്സ്പോർട്ട് പരാജയപ്പെട്ടു' : 'Export failed');
    }
  };

  const exportNutritionAll = async ({ sections, fileType, dateIso, isConsolidated = false }) => {
    if (!sections?.length) {
      toastError(useMalayalam ? 'എക്സ്പോർട്ടിന് ഡാറ്റയില്ല' : 'No data to export');
      return;
    }
    const title = isConsolidated
      ? `${labels.navNutrition} ${labels.reportTodayConsolidated} ${dateIso}`
      : `${labels.navNutrition} ${dateIso}`;
    try {
      const { blob, mimeType, extension } = createNutritionAllExportBlob({
        fileType,
        title,
        dateIso,
        sections,
        pdfMeta: {
          appName: labels.appTitle || 'NMhelper',
          schoolName: userInfo?.name || userInfo?.username || '',
          exportedAt: dateIso,
        },
      });
      const mode = isConsolidated ? 'consolidated' : 'by_division';
      await exportFile({
        blob,
        fileName: `nutrition_all_${mode}_${dateIso}.${extension}`,
        mimeType,
      });
      toastSuccess(labels.toastExportDone);
    } catch (err) {
      console.error('Nutrition export all failed:', err);
      toastError(useMalayalam ? 'എക്സ്പോർട്ട് പരാജയപ്പെട്ടു' : 'Export failed');
    }
  };

  // Example: exportData
  const exportData = async (dataToExport, fileType) => {
    if (!token || !dataToExport || dataToExport.length === 0) {
        toastError("No data available to export.");
        return;
    }

    let allClasses = [];
    try {
      // Fetch current class list to ensure all divisions are included
      const res = await api.get(`/head/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      allClasses = res.data;
    } catch (err) {
      console.error('Failed to fetch classes for export:', err);
      toastError("Could not fetch the full class list; export may be incomplete.");
      // Decide if you want to proceed with potentially incomplete data or stop
      // return;
    }

    const classGroups = {};
    const reportedDivisions = new Set(dataToExport.map(item => item.division_name));
    const exportRows = [];
    const dateOfReportRaw = dataToExport.length > 0 ? new Date(dataToExport[0].date) : new Date();
    // Format date as YYYY-MM-DD to avoid invalid characters in sheet name
    const dateOfReport = dateOfReportRaw.toISOString().split('T')[0];

    // Add reported divisions
    dataToExport.forEach(item => {
      const classNumMatch = item.division_name.match(/\d+/); // Extract first number sequence
      const classNum = classNumMatch ? classNumMatch[0] : 'Unknown';

      if (!classGroups[classNum]) {
        classGroups[classNum] = { boys: 0, girls: 0, divisions: new Set() };
      }
      const currentBoys = parseInt(item.boys) || 0;
      const currentGirls = parseInt(item.girls) || 0;
      classGroups[classNum].boys += currentBoys;
      classGroups[classNum].girls += currentGirls;
      classGroups[classNum].divisions.add(item.division_name);

      exportRows.push({
        [labels.division]: item.division_name,
        [labels.boys]: currentBoys,
        [labels.girls]: currentGirls,
        'Total': currentBoys + currentGirls,
        'Type': 'Division',
        [labels.status]: labels.updated
      });
    });

    // Add non-reported divisions from the full class list
    allClasses.forEach(cls => {
      if (!reportedDivisions.has(cls.division_name)) {
        const classNumMatch = cls.division_name.match(/\d+/);
        const classNum = classNumMatch ? classNumMatch[0] : 'Unknown';

        if (!classGroups[classNum]) {
          classGroups[classNum] = { boys: 0, girls: 0, divisions: new Set() };
        }
        // Ensure division is tracked even if not reported
        classGroups[classNum].divisions.add(cls.division_name);

        exportRows.push({
          [labels.division]: cls.division_name,
          [labels.boys]: 0,
          [labels.girls]: 0,
          'Total': 0,
          'Type': 'Division',
          [labels.status]: labels.notUpdated
        });
      }
    });

    // Sort rows primarily by class number, then by division name (handling potential non-numeric parts)
    exportRows.sort((a, b) => {
        const aName = a[labels.division];
        const bName = b[labels.division];
        const aNumMatch = aName.match(/\d+/);
        const bNumMatch = bName.match(/\d+/);
        const aNum = aNumMatch ? parseInt(aNumMatch[0]) : Infinity;
        const bNum = bNumMatch ? parseInt(bNumMatch[0]) : Infinity;

        if (aNum !== bNum) return aNum - bNum;
        // If numbers are the same or both non-numeric, sort alphabetically
        return aName.localeCompare(bName);
    });


    // Prepare final rows including class totals
    const finalExportRows = [];
    let overallBoys = 0;
    let overallGirls = 0;

    Object.keys(classGroups).sort((a, b) => {
        // Sort class groups numerically
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        if (!isNaN(numA)) return -1; // Numbers first
        if (!isNaN(numB)) return 1;
        return a.localeCompare(b); // Fallback alpha sort
    }).forEach(classNum => {
        const group = classGroups[classNum];
        // Add divisions for this class number first
        exportRows.filter(row => {
            const rowClassNumMatch = row[labels.division].match(/\d+/);
            const rowClassNum = rowClassNumMatch ? rowClassNumMatch[0] : 'Unknown';
            return rowClassNum === classNum && row.Type === 'Division';
        }).forEach(row => finalExportRows.push(row));

        // Add class total row
        const classTotalBoys = group.boys;
        const classTotalGirls = group.girls;
        finalExportRows.push({
            [labels.division]: `Class ${classNum} Total`,
            [labels.boys]: classTotalBoys,
            [labels.girls]: classTotalGirls,
            'Total': classTotalBoys + classTotalGirls,
            'Type': 'Class Total',
            [labels.status]: (classTotalBoys + classTotalGirls > 0) ? labels.updated : labels.notUpdated // Consider total updated if any division reported
        });
        overallBoys += classTotalBoys;
        overallGirls += classTotalGirls;
    });

    // Add Grand Total Row
     finalExportRows.push({}); // Blank row separator
     finalExportRows.push({
        [labels.division]: `Grand Total`,
        [labels.boys]: overallBoys,
        [labels.girls]: overallGirls,
        'Total': overallBoys + overallGirls,
        'Type': 'Grand Total',
        [labels.status]: ''
    });


    const headerOrder = [labels.division, labels.boys, labels.girls, 'Total', 'Type', labels.status];
    const { blob, mimeType } = createExportBlob({
      fileType,
      title: `NM Present ${dateOfReport}`,
      headers: headerOrder,
      rows: finalExportRows,
      pdfOrientation: 'auto',
      pdfMeta: {
        appName: labels.appTitle || 'NMhelper',
        schoolName: userInfo?.name || userInfo?.username || '',
        exportedAt: dateOfReport,
      },
    });

    try {
      await exportFile({
        blob,
        fileName: `nm_present_${dateOfReport.replace(/\//g, '-')}.${getExportExtension(fileType)}`,
        mimeType,
      });
      toastSuccess(labels.toastExportDone);
    } catch (err) {
      console.error('Export failed:', err);
      toastError(useMalayalam ? 'എക്സ്പോർട്ട് പരാജയപ്പെട്ടു' : 'Export failed');
    }
  };

  const formatMonthDateHeader = (isoDate) => {
    const [, m, d] = isoDate.split('-').map(Number);
    const day = String(d).padStart(2, '0');
    const mon = String(m).padStart(2, '0');
    return `${day}/${mon}`;
  };

  const fetchMonthlyReport = async (yearMonth) => {
    if (!token) return;
    const ym = yearMonth || selectedMonth;
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) {
      toastError(useMalayalam ? 'മാസം തിരഞ്ഞെടുക്കുക' : 'Please select a month');
      return;
    }
    localStorage.setItem(HEAD_HISTORY_MONTH_KEY, ym);
    const [year, month] = ym.split('-').map((n) => parseInt(n, 10));
    setIsLoading((prev) => ({ ...prev, monthlyReport: true }));
    try {
      const res = await api.get('/head/monthly-report', {
        params: { year, month },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Failed to load monthly report');
      }
      setMonthlyReport(res.data);
      setSelectedMonth(ym);
    } catch (err) {
      console.error('Fetch monthly report error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to load monthly report');
      setMonthlyReport(null);
    } finally {
      setIsLoading((prev) => ({ ...prev, monthlyReport: false }));
    }
  };

  const exportMonthlyReport = async (fileType) => {
    if (!monthlyReport?.rows) {
      toastError(useMalayalam ? 'ഏറ്റെടുക്കാൻ ഡാറ്റയില്ല' : 'No monthly data to export. Tap View first.');
      return;
    }
    const { dates, rows, footer, year, month } = monthlyReport;
    const classLabel = labels.division;
    const boysLabel = labels.boys;
    const girlsLabel = labels.girls;

    const headerRow = { [classLabel]: classLabel };
    dates.forEach((d) => {
      const h = formatMonthDateHeader(d);
      headerRow[`${h} ${boysLabel}`] = `${h} ${boysLabel}`;
      headerRow[`${h} ${girlsLabel}`] = `${h} ${girlsLabel}`;
    });
    headerRow[`${labels.rowTotal} ${boysLabel}`] = `${labels.rowTotal} ${boysLabel}`;
    headerRow[`${labels.rowTotal} ${girlsLabel}`] = `${labels.rowTotal} ${girlsLabel}`;
    headerRow[labels.totalStudents] = labels.totalStudents;

    const sheetRows = rows.map((row) => {
      const out = { [classLabel]: row.division_name };
      dates.forEach((d) => {
        const h = formatMonthDateHeader(d);
        const cell = row.byDate?.[d] || { boys: 0, girls: 0 };
        out[`${h} ${boysLabel}`] = cell.boys;
        out[`${h} ${girlsLabel}`] = cell.girls;
      });
      out[`${labels.rowTotal} ${boysLabel}`] = row.rowTotalBoys;
      out[`${labels.rowTotal} ${girlsLabel}`] = row.rowTotalGirls;
      out[labels.totalStudents] = row.rowTotal;
      return out;
    });

    const footerRow = { [classLabel]: labels.grandTotal };
    dates.forEach((d) => {
      const h = formatMonthDateHeader(d);
      const cell = footer.byDate?.[d] || { boys: 0, girls: 0 };
      footerRow[`${h} ${boysLabel}`] = cell.boys;
      footerRow[`${h} ${girlsLabel}`] = cell.girls;
    });
    footerRow[`${labels.rowTotal} ${boysLabel}`] = footer.grandTotalBoys;
    footerRow[`${labels.rowTotal} ${girlsLabel}`] = footer.grandTotalGirls;
    footerRow[labels.totalStudents] = footer.grandTotal;

    const colOrder = Object.keys(headerRow);
    const dataRows = [...sheetRows, {}, footerRow];
    const { blob, mimeType } = createExportBlob({
      fileType,
      title: `Monthly NM Present ${year}-${String(month).padStart(2, '0')}`,
      headers: colOrder,
      rows: dataRows,
      pdfOrientation: 'auto',
      pdfMeta: {
        appName: labels.appTitle || 'NMhelper',
        schoolName: userInfo?.name || userInfo?.username || '',
        exportedAt: `${year}-${String(month).padStart(2, '0')}`,
      },
    });

    try {
      await exportFile({
        blob,
        fileName: `monthly_nm_present_${year}-${String(month).padStart(2, '0')}.${getExportExtension(fileType)}`,
        mimeType,
      });
      toastSuccess(labels.toastExportDone);
    } catch (err) {
      console.error('Monthly export failed:', err);
      toastError(useMalayalam ? 'എക്സ്പോർട്ട് പരാജയപ്പെട്ടു' : 'Export failed');
    }
  };

  const exportDayHistoryData = async ({
    rows,
    fileType,
    dateIso,
    isConsolidated = false,
  }) => {
    if (!rows?.length) {
      toastError(useMalayalam ? 'എക്സ്പോർട്ടിന് ഡാറ്റയില്ല' : 'No data to export');
      return;
    }

    const title = isConsolidated
      ? `Day Consolidated ${dateIso}`
      : `Day History ${dateIso}`;
    const headers = isConsolidated
      ? [labels.classNumber, labels.boys, labels.girls, labels.totalStudents]
      : [labels.division, labels.boys, labels.girls, labels.totalStudents];

    const exportRows = rows.map((row) =>
      isConsolidated
        ? {
            [labels.classNumber]: row.group,
            [labels.boys]: row.boys,
            [labels.girls]: row.girls,
            [labels.totalStudents]: row.total,
          }
        : {
            [labels.division]: row.division_name,
            [labels.boys]: row.boys,
            [labels.girls]: row.girls,
            [labels.totalStudents]: row.total,
          }
    );

    const { blob, mimeType } = createExportBlob({
      fileType,
      title,
      headers,
      rows: exportRows,
      pdfOrientation: 'auto',
      pdfMeta: {
        appName: labels.appTitle || 'NMhelper',
        schoolName: userInfo?.name || userInfo?.username || '',
        exportedAt: dateIso,
      },
    });

    const safeDate = String(dateIso || new Date().toISOString().slice(0, 10)).replace(/\//g, '-');
    const prefix = isConsolidated ? 'day_consolidated' : 'day_history';
    try {
      await exportFile({
        blob,
        fileName: `${prefix}_${safeDate}.${getExportExtension(fileType)}`,
        mimeType,
      });
      toastSuccess(labels.toastExportDone);
    } catch (err) {
      console.error('Day export failed:', err);
      toastError(useMalayalam ? 'എക്സ്പോർട്ട് പരാജയപ്പെട്ടു' : 'Export failed');
    }
  };

  // Example: fetchSchoolHeads
  const fetchSchoolHeads = async (currentToken = token) => {
    if (!currentToken) return;
    setIsLoading(prev => ({...prev, heads: true}));
    try {
      const res = await api.get(`/admin/school-heads`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      // Sort school heads by name ascending for consistent order
      const normalized = (res.data || []).map((h) => ({
        ...h,
        divisions: Math.max(0, parseInt(h.divisions, 10) || 0),
        subscription_expires_at: h.subscription_expires_at
          ? String(h.subscription_expires_at).slice(0, 10)
          : null,
      }));
      const sortedHeads = normalized.slice().sort((a, b) => {
        if (a.account_role === 'admin') return -1;
        if (b.account_role === 'admin') return 1;
        if (a.username === 'admin') return -1;
        if (b.username === 'admin') return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      setSchoolHeads(sortedHeads);
    } catch (err) {
      console.error("Fetch school heads error:", err);
      toastError(err.response?.data?.error || err.message || 'Failed to fetch school heads');
      setSchoolHeads([]); // Clear on error
    } finally {
      setIsLoading(prev => ({...prev, heads: false}));
    }
  };

  const fetchTesterSignups = async (query = '', currentToken = token) => {
    if (!currentToken) return [];
    setIsLoading((prev) => ({ ...prev, testerSignups: true }));
    try {
      const res = await api.get('/admin/tester-signups', {
        params: { q: query || '' },
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const rows = res.data?.rows || [];
      setTesterSignups(rows);
      return rows;
    } catch (err) {
      console.error('Fetch tester signups error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to fetch tester signups');
      setTesterSignups([]);
      return [];
    } finally {
      setIsLoading((prev) => ({ ...prev, testerSignups: false }));
    }
  };

  const exportTesterSignups = async (format = 'csv') => {
    if (!token) return false;
    try {
      const res = await api.get('/admin/tester-signups/export', {
        params: { format },
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const fileName = format === 'txt' ? 'tester-emails.txt' : 'tester-signups.csv';
      const blob = new Blob([res.data], {
        type: format === 'txt' ? 'text/plain;charset=utf-8;' : 'text/csv;charset=utf-8;',
      });
      await exportFile({ blob, fileName, mimeType: blob.type });
      toastSuccess(labels.toastExportDone);
      return true;
    } catch (err) {
      console.error('Export tester signups error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to export tester list');
      return false;
    }
  };

  const fetchInvoiceIssuer = async (currentToken = token) => {
    if (!currentToken) {
      const cached = mergeInvoiceIssuer({});
      setInvoiceIssuer(cached);
      return cached;
    }
    setIsLoading((prev) => ({ ...prev, invoiceIssuer: true }));
    try {
      const res = await api.get('/admin/invoice-settings', {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const issuer = normalizeInvoiceIssuer(res.data?.issuer || getDefaultInvoiceIssuer());
      writeCachedInvoiceIssuer(issuer);
      setInvoiceIssuer(issuer);
      return issuer;
    } catch (err) {
      console.error('Fetch invoice issuer error:', err);
      const fallback = mergeInvoiceIssuer({});
      setInvoiceIssuer(fallback);
      toastError(err.response?.data?.error || err.message || 'Failed to load invoice settings');
      return fallback;
    } finally {
      setIsLoading((prev) => ({ ...prev, invoiceIssuer: false }));
    }
  };

  const saveInvoiceIssuer = async (issuerData, currentToken = token) => {
    if (!currentToken) return false;
    setIsLoading((prev) => ({ ...prev, invoiceIssuer: true }));
    try {
      const payload = pickIssuerPayload(issuerData);
      const res = await api.put('/admin/invoice-settings', payload, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const issuer = normalizeInvoiceIssuer(res.data?.issuer || payload);
      writeCachedInvoiceIssuer(issuer);
      setInvoiceIssuer(issuer);
      toastSuccess(labels.toastInvoiceIssuerSaved);
      return issuer;
    } catch (err) {
      console.error('Save invoice issuer error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to save invoice settings');
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, invoiceIssuer: false }));
    }
  };

  const copyTesterEmails = async () => {
    const emails = Array.from(
      new Set((testerSignups || []).map((r) => String(r.email || '').trim()).filter(Boolean))
    );
    if (emails.length === 0) {
      toastError(useMalayalam ? 'കോപ്പി ചെയ്യാൻ ഇമെയിൽ ഇല്ല' : 'No emails to copy');
      return false;
    }
    const text = emails.join(', ');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toastSuccess(useMalayalam ? 'ഇമെയിൽ പട്ടിക കോപ്പി ചെയ്തു' : 'Tester email list copied');
      return true;
    } catch (err) {
      console.error('Copy tester emails failed:', err);
      toastError(useMalayalam ? 'കോപ്പി ചെയ്യാനായില്ല' : 'Failed to copy emails');
      return false;
    }
  };

  // Pause head function
  const pauseHead = async (headId) => {
    if (!token) return;
    setIsLoading(prev => ({ ...prev, pauseContinueHead: true }));
    try {
      const res = await api.post(`/admin/pause-head/${headId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toastSuccess(labels.toastHeadPaused);
        await fetchSchoolHeads();
      } else {
        toastError(res.data.error || 'Failed to pause head');
      }
    } catch (err) {
      console.error('Pause head error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to pause head');
    } finally {
      setIsLoading(prev => ({ ...prev, pauseContinueHead: false }));
    }
  };

  // Continue head function
  const continueHead = async (headId) => {
    if (!token) return;
    setIsLoading(prev => ({ ...prev, pauseContinueHead: true }));
    try {
      const res = await api.post(`/admin/continue-head/${headId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toastSuccess(labels.toastHeadResumed);
        await fetchSchoolHeads();
      } else {
        toastError(res.data.error || 'Failed to continue head');
      }
    } catch (err) {
      console.error('Continue head error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to continue head');
    } finally {
      setIsLoading(prev => ({ ...prev, pauseContinueHead: false }));
    }
  };

  // Example: addTeacher
  const addTeacher = async () => {
    if (subscriptionWritesBlocked) {
      blockSubscriptionWrite();
      return;
    }
    if (!token) return;
    if (!newTeacher.name || !newTeacher.login_suffix?.trim() || !newTeacher.password || !newTeacher.class_id) {
      toastError(useMalayalam ? 'ദയവായി എല്ലാ അദ്ധ്യാപക ഫീൽഡുകളും പൂരിപ്പിക്കുക' : 'Please fill all teacher fields, including pen/class ID and class');
      return;
    }
    setIsLoading(prev => ({ ...prev, addTeacher: true }));
    try {
      // First add the teacher
      const addRes = await api.post(`/head/add-teacher`, {
        name: newTeacher.name,
        login_suffix: newTeacher.login_suffix.trim(),
        password: newTeacher.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!addRes.data.success || !addRes.data.id) {
        throw new Error(addRes.data.error || 'Failed to add teacher');
      }

      // Then assign the teacher to the class
      const assignRes = await api.post(`/head/assign-teacher`, {
        teacher_id: addRes.data.id,
        class_id: newTeacher.class_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!assignRes.data.success) {
        throw new Error(assignRes.data.error || 'Failed to assign teacher to class');
      }

      toastSuccess(labels.toastTeacherAdded);
      setNewTeacher({ name: '', login_suffix: '', password: '', class_id: '' }); // Reset form
      await Promise.all([fetchTeachers(), fetchClasses()]); // Refresh lists

    } catch (err) {
      console.error('Add teacher error:', err);
      toastError(err.response?.data?.error || err.message || (useMalayalam ? 'അദ്ധ്യാപകനെ ചേർക്കുന്നതിൽ പിശക്' : 'Failed to add teacher'));
    } finally {
       setIsLoading(prev => ({ ...prev, addTeacher: false }));
    }
  };

  const assignTeacherToClass = async (teacherId, classId, replace = false) => {
    if (subscriptionWritesBlocked) {
      blockSubscriptionWrite();
      return false;
    }
    if (!token || !teacherId) return false;
    setIsLoading((prev) => ({ ...prev, assignTeacher: true }));
    try {
      const res = await api.post('/head/assign-teacher', {
        teacher_id: teacherId,
        class_id: classId,
        replace,
      });
      if (!res.data.success) {
        throw new Error(res.data.error || 'Failed to assign class');
      }
      toastSuccess(labels.toastClassAssigned);
      await Promise.all([fetchTeachers(), fetchClasses()]);
      return true;
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'CLASS_ALREADY_ASSIGNED') {
        const name = err.response?.data?.current_teacher_name || 'another teacher';
        const division = classes.find((c) => String(c.id) === String(classId))?.division_name || 'this class';
        const ok = await confirmDialog({
          title: labels.replaceTeacherTitle,
          message: labels.replaceTeacherConfirm
            .replace('{class}', division)
            .replace('{name}', name),
          confirmLabel: labels.replaceTeacherBtn,
          danger: true,
        });
        if (ok) return assignTeacherToClass(teacherId, classId, true);
      }
      toastError(err.response?.data?.error || err.message || 'Failed to assign class');
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, assignTeacher: false }));
    }
  };

  const applyTeacherClassToForm = (cls) => {
    if (!cls) return;
    setStrengthForm({
      class_id: cls.id || '',
      division_name: cls.division_name || 'N/A',
      boys: cls.boys ?? cls.strength?.boys ?? '',
      girls: cls.girls ?? cls.strength?.girls ?? '',
      max_students: cls.max_students || '',
      max_boys: cls.max_boys ?? '',
      max_girls: cls.max_girls ?? '',
    });
    localStorage.setItem('nmhelper_teacher_class_id', String(cls.id));
  };

  const loadTeacherClasses = async (currentToken = token, preferredClassId) => {
    const authToken = currentToken || token;
    if (!authToken) return [];
    const res = await api.get('/teacher/classes', {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const list = sortByDivisionName(res.data?.classes || []);
    setTeacherClasses(list);
    if (list.length === 0) {
      setStrengthForm({ class_id: '', division_name: '', boys: '', girls: '', max_students: '', max_boys: '', max_girls: '' });
      localStorage.removeItem('nmhelper_teacher_class_id');
      return list;
    }
    const stored =
      preferredClassId ||
      localStorage.getItem('nmhelper_teacher_class_id');
    const active =
      list.find((c) => String(c.id) === String(stored)) || list[0];
    applyTeacherClassToForm(active);
    return list;
  };

  const selectTeacherClass = async (classId) => {
    const fromList = teacherClasses.find((c) => String(c.id) === String(classId));
    if (fromList) {
      applyTeacherClassToForm(fromList);
      return;
    }
    try {
      const res = await api.get('/teacher/class', { params: { class_id: classId } });
      if (res.data) {
        applyTeacherClassToForm({
          ...res.data,
          boys: res.data.strength?.boys,
          girls: res.data.strength?.girls,
        });
        await loadTeacherClasses(token, classId);
      }
    } catch (err) {
      console.error('selectTeacherClass:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to load class');
    }
  };

  const unassignTeacherFromClass = async (teacherId, classId = null) => {
    if (!token || !teacherId) return false;
    setIsLoading((prev) => ({ ...prev, assignTeacher: true }));
    try {
      const body = { teacher_id: teacherId };
      if (classId != null) body.class_id = classId;
      const res = await api.post('/head/unassign-teacher', body);
      if (!res.data.success) throw new Error(res.data.error || 'Failed to unassign');
      toastSuccess(labels.toastClassUnassigned);
      await Promise.all([fetchTeachers(), fetchClasses()]);
      return true;
    } catch (err) {
      toastError(err.response?.data?.error || err.message || 'Failed to unassign class');
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, assignTeacher: false }));
    }
  };

  // FIX: Use api for deleteTeacher
  const resetTeacherPassword = async (teacherId, newPassword) => {
    if (subscriptionWritesBlocked) {
      blockSubscriptionWrite();
      return false;
    }
    if (!token || !teacherId) return false;
    setIsLoading((prev) => ({ ...prev, resetTeacherPassword: true }));
    try {
      const res = await api.post(
        `/head/reset-teacher-password/${teacherId}`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Failed to reset password');
      }
      toastSuccess(labels.toastTeacherPasswordReset);
      return true;
    } catch (err) {
      toastError(err.response?.data?.error || err.message || 'Failed to reset password');
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, resetTeacherPassword: false }));
    }
  };

  const updateTeacherName = async (teacherId, name) => {
    if (subscriptionWritesBlocked) {
      blockSubscriptionWrite();
      return false;
    }
    const trimmed = String(name ?? '').trim();
    if (!token || !teacherId || !trimmed) {
      toastError(useMalayalam ? 'അദ്ധ്യാപകന്റെ പേര് നൽകുക' : 'Please enter a teacher name');
      return false;
    }
    setIsLoading((prev) => ({ ...prev, updateTeacherName: true }));
    try {
      const res = await api.patch(
        `/head/teachers/${teacherId}`,
        { name: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Failed to update teacher name');
      }
      toastSuccess(labels.toastTeacherNameUpdated);
      await Promise.all([fetchTeachers(), fetchClasses()]);
      return true;
    } catch (err) {
      toastError(err.response?.data?.error || err.message || (useMalayalam ? 'പേര് മാറ്റുന്നതിൽ പിശക്' : 'Failed to update teacher name'));
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, updateTeacherName: false }));
    }
  };

  const headUpdateStrength = async (cls, boysRaw, girlsRaw) => {
    if (subscriptionWritesBlocked) {
      blockSubscriptionWrite();
      return false;
    }
    if (!token || !cls?.id) return false;

    const boys = parseInt(boysRaw, 10);
    const girls = parseInt(girlsRaw, 10);
    if (isNaN(boys) || boys < 0 || isNaN(girls) || girls < 0) {
      toastError(useMalayalam ? 'ദയവായി ആൺകുട്ടികളുടെയും പെൺകുട്ടികളുടെയും എണ്ണം ശരിയായി നൽകുക' : 'Please enter valid numbers for boys and girls.');
      return false;
    }

    const maxBoys = parseInt(cls.max_boys, 10) || 0;
    const maxGirls = parseInt(cls.max_girls, 10) || 0;
    const maxStudents = parseInt(cls.max_students, 10) || 0;
    if (maxBoys > 0 && boys > maxBoys) {
      toastError(labels.toastBoysCapExceeded + (useMalayalam ? ` (${maxBoys})` : ` (max ${maxBoys})`));
      return false;
    }
    if (maxGirls > 0 && girls > maxGirls) {
      toastError(labels.toastGirlsCapExceeded + (useMalayalam ? ` (${maxGirls})` : ` (max ${maxGirls})`));
      return false;
    }
    if (maxBoys === 0 && maxGirls === 0 && maxStudents > 0 && boys + girls > maxStudents) {
      toastError(
        useMalayalam
          ? `ആകെ ${maxStudents} ൽ കൂടുതൽ നൽകാൻ കഴിയില്ല`
          : `Total cannot exceed ${maxStudents} students`
      );
      return false;
    }

    setIsLoading((prev) => ({ ...prev, headUpdateStrength: true }));
    try {
      const res = await api.post(
        '/head/update-strength',
        {
          class_id: cls.id,
          boys,
          girls,
          date: getIstDateIso(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.data?.success) {
        throw new Error(res.data?.message || res.data?.error || 'Failed to save strength');
      }
      toastSuccess(labels.toastStrengthSaved);
      await Promise.all([fetchClasses(), fetchReport(), fetchStrengthStatus()]);
      return true;
    } catch (err) {
      toastError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          (useMalayalam ? 'NM ഹാജർ സേവ് ചെയ്യുന്നതിൽ പിശക്' : 'Failed to save NM present')
      );
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, headUpdateStrength: false }));
    }
  };

  const resetHeadPassword = async (headId, newPassword) => {
    if (!token || !headId) return false;
    setIsLoading((prev) => ({ ...prev, resetHeadPassword: true }));
    try {
      const res = await api.post(
        `/admin/reset-head-password/${headId}`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Failed to reset password');
      }
      toastSuccess(labels.toastHeadPasswordReset);
      return true;
    } catch (err) {
      toastError(err.response?.data?.error || err.message || 'Failed to reset password');
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, resetHeadPassword: false }));
    }
  };

  const deleteTeacher = async (teacherId, teacherName) => {
    if (!token) return;
    if (!await confirmDialog(useMalayalam ? `${teacherName}-നെ ഇല്ലാതാക്കണോ?` : `Delete teacher ${teacherName}? This cannot be undone.`)) {
      return;
    }
    setIsLoading(prev => ({ ...prev, deleteTeacher: true }));
    try {
      const res = await api.delete(`/head/delete-teacher/${teacherId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toastSuccess(labels.toastTeacherRemoved);
        // Refresh lists
        await Promise.all([fetchTeachers(), fetchClasses()]);
      } else {
        throw new Error(res.data.error || 'Deletion failed');
      }
    } catch (err) {
      console.error('Delete teacher error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to delete teacher');
    } finally {
       setIsLoading(prev => ({ ...prev, deleteTeacher: false }));
    }
  };

  // FIX: Use api for deleteClass
  const deleteClass = async (classId, divisionName) => {
    if (!token) return;
    if (!await confirmDialog(useMalayalam ? `${divisionName} ക്ലാസ് ഇല്ലാതാക്കണോ? ഇത് ചരിത്ര ഡാറ്റയും ഇല്ലാതാക്കും.` : `Delete class ${divisionName}? This will also remove its historical data and unassign any teacher. This cannot be undone.`)) {
      return;
    }
     setIsLoading(prev => ({ ...prev, deleteClass: true }));
    try {
      // Backend should handle deleting related historical data and unassigning teacher
      const res = await api.delete(`/head/delete-class/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        toastSuccess(labels.toastClassRemoved);
        await fetchClasses(); // Refresh class list
        // Optionally refresh teachers if assignment changes
        await fetchTeachers();
      } else {
        throw new Error(res.data.error || 'Deletion failed');
      }
    } catch (err) {
      console.error('Delete class error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to delete class');
    } finally {
       setIsLoading(prev => ({ ...prev, deleteClass: false }));
    }
  };

  // Example: addClass
  const addClass = async () => {
    if (subscriptionWritesBlocked) {
      blockSubscriptionWrite();
      return;
    }
    if (!token) return;
    if (!isValidClassLevel(newClass.class_number)) {
      toastError(
        useMalayalam ? 'LKG, UKG, അല്ലെങ്കിൽ 1–10 തിരഞ്ഞെടുക്കുക' : 'Please select class (LKG, UKG, or 1–10)'
      );
      return;
    }
    const divisionLetter = newClass.division_letter.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!divisionLetter) {
      toastError(useMalayalam ? 'ഡിവിഷൻ നൽകുക (ഉദാ. A, B, AA)' : 'Please enter division (e.g. A, B, AA)');
      return;
    }
    const divisionName = buildDivisionName(newClass.class_number, divisionLetter);
    const maxBoys = parseInt(newClass.max_boys, 10);
    const maxGirls = parseInt(newClass.max_girls, 10);
    if (
      newClass.max_boys === '' ||
      newClass.max_girls === '' ||
      isNaN(maxBoys) ||
      isNaN(maxGirls) ||
      maxBoys < 0 ||
      maxGirls < 0
    ) {
      toastError(
        useMalayalam ? 'പരമാവധി ആൺ, പെൺ എണ്ണം നൽകുക' : 'Please enter max boys and max girls'
      );
      return;
    }
    if (maxBoys === 0 && maxGirls === 0) {
      toastError(
        useMalayalam
          ? 'ആൺ അല്ലെങ്കിൽ പെൺ പരമാവധി കുറഞ്ഞത് 1 ആയിരിക്കണം'
          : 'At least one of max boys or max girls must be greater than 0'
      );
      return;
    }

    setIsLoading(prev => ({ ...prev, addClass: true }));
    try {
      const res = await api.post(`/head/add-class`, {
        division_name: divisionName,
        class_number: newClass.class_number,
        division_letter: divisionLetter,
        max_boys: maxBoys,
        max_girls: maxGirls,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success && res.data.id) {
        toastSuccess(labels.toastClassAdded);
        setNewClass({ class_number: '', division_letter: '', teacher_id: '', max_boys: '', max_girls: '' });
        setClasses((prev) =>
          sortByDivisionName([
            ...prev,
            {
              id: res.data.id,
              division_name: divisionName,
              max_students: res.data.max_students ?? maxBoys + maxGirls,
              max_boys: res.data.max_boys ?? maxBoys,
              max_girls: res.data.max_girls ?? maxGirls,
              teacher_id: null,
              teacher_name: 'Not assigned',
            },
          ])
        );
        // Optionally trigger a full refresh later or rely on immediate update
        // await fetchClasses();
      } else {
        throw new Error(res.data.error || 'Class creation failed or did not return ID');
      }
    } catch (err) {
      console.error('Add class error:', err);
      const errorMsg = err.response?.data?.error || err.message;
      if (errorMsg && errorMsg.toLowerCase().includes('duplicate')) {
        toastError(useMalayalam ? 'ഈ ഡിവിഷൻ പേര് ഇതിനകം നിലവിലുണ്ട് (ബാക്ക്‌എൻഡ് പിശക്)' : 'This division name already exists (backend error)');
      } else {
        toastError(errorMsg || (useMalayalam ? 'ക്ലാസ് ചേർക്കുന്നതിൽ പിശക്' : 'Failed to add class'));
      }
    } finally {
        setIsLoading(prev => ({ ...prev, addClass: false }));
    }
  };

  // Example: addSchoolHead
  const addSchoolHead = async () => {
    if (!token) return;
    if (!newHead.name || !newHead.username || !newHead.password || newHead.divisions === '') {
      toastError(useMalayalam ? 'ദയവായി എല്ലാ ഫീൽഡുകളും പൂരിപ്പിക്കുക' : 'Please fill all fields, including number of divisions');
      return;
    }
    const divisionsNum = parseInt(newHead.divisions, 10);
    if (isNaN(divisionsNum) || divisionsNum < 0) {
      toastError(useMalayalam ? 'സാധുവായ ഡിവിഷൻ എണ്ണം നൽകുക' : 'Enter a valid number of divisions (0 or more)');
      return;
    }
    setIsLoading(prev => ({...prev, addHead: true}));
    try {
      const res = await api.post(
        `/admin/add-head`,
        {
          name: newHead.name,
          username: newHead.username,
          password: newHead.password,
          divisions: divisionsNum,
          numberOfDivisions: divisionsNum,
          subscription_expires_at:
            newHead.subscriptionExpiresAt?.trim() || defaultTrialEndDate(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
          toastSuccess(labels.toastHeadAdded);
          const created = res.data.head || {
            id: res.data.id,
            name: newHead.name,
            username: newHead.username,
            divisions: res.data.divisions ?? divisionsNum,
            paused: false,
            account_role: 'head',
          };
          setSchoolHeads((prev) => {
            const without = prev.filter((h) => h.id !== created.id);
            return [...without, { ...created, divisions: divisionsNum }].sort((a, b) =>
              (a.name || '').localeCompare(b.name || '')
            );
          });
          setNewHead({
            name: '',
            username: '',
            password: '',
            divisions: '',
            subscriptionExpiresAt: defaultTrialEndDate(),
          });
          setShowAddHeadForm(false);
          await fetchSchoolHeads();
      } else {
          throw new Error(res.data.error || 'Failed to add school head');
      }
    } catch (err) {
      console.error("Add head error:", err);
      toastError(err.response?.data?.error || err.message || 'Failed to add school head');
    } finally {
      setIsLoading(prev => ({...prev, addHead: false}));
    }
  };

  // FIX: Use api for deleteSchoolHead
  const deleteSchoolHead = async (headId, headName) => {
      if (!token) return;
      if (await confirmDialog(`Delete school head ${headName}? All their classes and teachers will also be removed. This cannot be undone.`)) {
          setIsLoading(prev => ({ ...prev, deleteHead: true }));
          try {
              const res = await api.delete(`/admin/delete-head/${headId}`, {
                  headers: { Authorization: `Bearer ${token}` }
              });
              if (res.data.success) {
                  const { teachers = 0, classes = 0 } = res.data.deleted || {};
                  toastSuccess(
                    teachers || classes
                      ? `${labels.toastHeadRemoved} (${teachers} teacher(s), ${classes} class(es)).`
                      : labels.toastHeadRemoved
                  );
                  setSchoolHeads(prevHeads => prevHeads.filter(h => h.id !== headId));
              } else {
                  throw new Error(res.data.error || 'Deletion failed');
              }
          } catch (err) {
              console.error('Delete head error:', err);
              toastError(err.response?.data?.error || err.message || 'Failed to delete school head.');
          } finally {
              setIsLoading(prev => ({ ...prev, deleteHead: false }));
          }
      }
  };

  // Example: setDivisionsForHead (Admin)
  const setDivisionsForHead = async (username, divisions) => {
      if (!token || !username || divisions === undefined) return;
      const numDivisions = parseInt(divisions);
      if (isNaN(numDivisions) || numDivisions < 0) {
          toastError('Please enter a valid non-negative number for divisions.');
          return;
      }

      setIsLoading(prev => ({ ...prev, setDivisions: true }));
      try {
          const res = await api.post(
              `/admin/set-divisions`,
              { username: username, numberOfDivisions: numDivisions },
              { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.success) {
              toastSuccess(labels.toastDivisionsSaved);
              // Update state locally
              setHeadDivisions(
                headDivisions.map((h) =>
                  h.username === username ? { ...h, divisions: numDivisions } : h
                )
              ); // Add a closing parenthesis here

              // Refresh school heads list to update UI
              await fetchSchoolHeads();

              // Clear form if using separate inputs
              setHeadUsernameForDivisions('');
              setNumberOfDivisions('');
              return true; // Indicate success
          } else {
              throw new Error(res.data.error || 'Failed to set divisions');
          }
      } catch (err) {
          console.error('Set divisions error:', err);
          toastError(err.response?.data?.error || err.message || 'Failed to set divisions');
          return false; // Indicate failure
      } finally {
          setIsLoading(prev => ({ ...prev, setDivisions: false }));
      }
  };

  const setSubscriptionForHead = async (headId, subscriptionExpiresAt) => {
    if (!token || !headId || !subscriptionExpiresAt) return false;
    setIsLoading((prev) => ({ ...prev, setSubscription: true }));
    try {
      const res = await api.post(
        '/admin/set-subscription',
        { id: headId, subscription_expires_at: subscriptionExpiresAt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess(labels.toastSubscriptionSaved);
        await fetchSchoolHeads();
        return true;
      }
      throw new Error(res.data.error || 'Failed to update subscription');
    } catch (err) {
      console.error('Set subscription error:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to update subscription');
      return false;
    } finally {
      setIsLoading((prev) => ({ ...prev, setSubscription: false }));
    }
  };

  // Example: fetchStrengthStatus
  const fetchStrengthStatus = async () => {
    if (!token) return;
    setIsLoadingStrengthStatus(true);
    try {
      // We need both the full class list and today's strength report
      const [classesRes, strengthRes] = await Promise.all([
        api.get('/head/classes'),
        api.get('/head/report', { params: { date: new Date().toISOString().split('T')[0] } }),
      ]);

      let currentClasses = classesRes.data || [];
      const todaysStrengths = strengthRes.data || [];
      const updatedClassIds = new Set(todaysStrengths.map(s => s.class_id));

      // Filter classes by logged-in head username if available
      // Removed filtering by head_username because backend does not return it
      // if (userInfo && userInfo.role === 'head') {
      //   currentClasses = currentClasses.filter(c => c.head_username === userInfo.username);
      // }

      const classesWithStatus = sortByDivisionName(
        currentClasses.map((cls) => ({
          ...cls,
          teacher_name: cls.teacher_id
            ? cls.teacher_name || `Assigned (ID: ${cls.teacher_id})`
            : 'Not assigned',
          isUpdated: updatedClassIds.has(cls.id),
        }))
      );

      setStrengthStatus(classesWithStatus);
    } catch (err) {
      console.error('Failed to fetch strength status:', err);
      toastError(err.response?.data?.error || err.message || 'Failed to fetch strength status');
      setStrengthStatus([]); // Clear on error
    } finally {
      setIsLoadingStrengthStatus(false);
    }
  };

  const fetchTeacherSubmissionStatus = async (dateIso) => {
    if (!token) return;
    const date = dateIso || getIstDateIso();
    setIsLoadingTeacherSubmission(true);
    try {
      const res = await api.get('/teacher/submission-status', { params: { date } });
      setTeacherSubmissionStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch teacher submission status:', err);
      setTeacherSubmissionStatus(null);
    } finally {
      setIsLoadingTeacherSubmission(false);
    }
  };

  const logout = (options = {}) => {
    const keepLoginError = Boolean(options.keepLoginError);
    unregisterPushToken().finally(() => {
      sessionBootstrapped.current = false;
      setToken('');
      storageRemove('nmhelper_token');
      storageRemove('nmhelper_user');
      setRole('');
      setLoginData({ username: '', password: '' });
      setStrengths([]);
      setHistoricalStrengths([]);
      setTeachers([]);
      setClasses([]);
      setSchoolHeads([]);
      setTesterSignups([]);
      setStrengthForm({ class_id: '', division_name: '', boys: '', girls: '', max_students: '', max_boys: '', max_girls: '' });
      setTeacherClasses([]);
      setTeacherSubmissionStatus(null);
      storageRemove('nmhelper_teacher_class_id');
      storageRemove(HEAD_HISTORY_DATE_KEY);
      storageRemove(HEAD_HISTORY_MONTH_KEY);
      if (!keepLoginError) {
        setLoginError('');
        setLoginErrorMeta(null);
      }
      setStatusMessage('');
    });
  };

  // One-time session restore after refresh (not re-run on every fetch fn identity change)
  useEffect(() => {
    if (!token || !userInfo?.role || sessionBootstrapped.current) return;

    sessionBootstrapped.current = true;
    let cancelled = false;

    const bootstrapSession = async () => {
      try {
        if (userInfo.role === 'admin') {
          await Promise.allSettled([
            fetchSchoolHeads(token),
            fetchTesterSignups('', token),
            fetchInvoiceIssuer(token),
          ]);
        } else if (userInfo.role === 'head') {
          const savedHistoryDate = localStorage.getItem(HEAD_HISTORY_DATE_KEY);
          if (savedHistoryDate && /^\d{4}-\d{2}-\d{2}$/.test(savedHistoryDate) && !cancelled) {
            setSelectedDate(savedHistoryDate);
          }
          await Promise.allSettled([
            fetchReport(token),
            fetchTeachers(token),
            fetchClasses(token),
          ]);
        } else if (userInfo.role === 'teacher') {
          await loadTeacherClasses(token);
          if (!cancelled) {
            const today = getIstDateIso();
            setSelectedDate(today);
            fetchTeacherSubmissionStatus(today);
          }
        }
      } catch (err) {
        console.error('Session bootstrap failed:', err);
      }
    };

    bootstrapSession();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userInfo?.role]);

  useEffect(() => {
    const onAuthExpired = () => {
      const msg = 'Session expired. Please log in again.';
      setLoginError(msg);
      logout({ keepLoginError: true });
    };
    window.addEventListener('nmhelper:auth-expired', onAuthExpired);
    return () => window.removeEventListener('nmhelper:auth-expired', onAuthExpired);
  }, []);


  return {
    online, pending, syncing, syncNow, refreshPending, lastSyncError,
    role, setRole, token, userInfo, setUserInfo, loginData, setLoginData,
    strengths, historicalStrengths, monthlyReport, selectedMonth, setSelectedMonth,
    selectedDate, setSelectedDate,
    teachers, classes, teacherClasses, teacherSchoolClasses, strengthForm, setStrengthForm,
    nutritionForm, setNutritionForm, nutritionRecords, nutritionReport,
    nutritionHistoricalDates, nutritionHistoricalReport,
    selectedNutritionDate, setSelectedNutritionDate,
    useMalayalam, setUseMalayalam,
    schoolHeads, setSchoolHeads, testerSignups, showAddHeadForm, setShowAddHeadForm, newHead, setNewHead,
    newTeacher, setNewTeacher, newClass, setNewClass, isLoading,
    strengthStatus, showStrengthStatus, setShowStrengthStatus, isLoadingStrengthStatus,
    headUsernameForDivisions, setHeadUsernameForDivisions, numberOfDivisions, setNumberOfDivisions,
    loginError, loginErrorMeta, statusMessage, setStatusMessage, labels,
    subscriptionExpiresAt, subscriptionWritesBlocked, subscriptionWarning,
    login, logout, fetchTeachers, fetchHistoricalReport, fetchMonthlyReport, deleteHistoricalReport,
    updateClassCapacity, fetchClasses, fetchReport, updateStrength,
    fetchTeacherNutrition, updateNutritionItem, updateNutritionMilk, clearNutritionItems,
    fetchNutritionReport, fetchNutritionHistoricalDates, fetchNutritionHistoricalReport,
    fetchTeacherHistoricalReport, fetchTeacherMonthlyReport, fetchTeacherSchoolClasses,
    fetchTeacherNutritionHistoricalDates, fetchTeacherNutritionHistoricalReport,
    deleteNutritionHistorical, exportNutritionData, exportNutritionAll,
    exportData, exportMonthlyReport, exportDayHistoryData,
    fetchSchoolHeads, pauseHead, continueHead, addTeacher, deleteTeacher, resetTeacherPassword, updateTeacherName, headUpdateStrength,
    resetHeadPassword, deleteClass,
    addClass, addSchoolHead, deleteSchoolHead, setDivisionsForHead, setSubscriptionForHead,
    fetchTesterSignups, exportTesterSignups, copyTesterEmails,
    invoiceIssuer, fetchInvoiceIssuer, saveInvoiceIssuer,
    fetchStrengthStatus,
    fetchTeacherSubmissionStatus,
    teacherSubmissionStatus,
    isLoadingTeacherSubmission,
    assignTeacherToClass, unassignTeacherFromClass, loadTeacherClasses, selectTeacherClass,
    toastSuccess, toastError,
  };
}
